import type { UI } from '@/v2/types';
import React from 'react';
import styled from '@emotion/styled';
import { Flex } from '@qga/roo-ui/components';

import Button from '@/components/Button';
import Modal from '@/v2/components/ui/Modal';
import PackageInclusions from '@/v2/components/ui/PackageInclusions';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import { AvailabilityApi } from '@/v2/services/availability/types';
import dataLayer from '@/v2/utils/dataLayer';
import OfferPrice from '../RoomOptions/components/RoomOption/components/CTA/components/OfferPrice';
import Pictures from '../RoomOptions/components/RoomOption/components/Pictures';
import Heading from './components/Heading';
import RoomType from './components/RoomType';

export type OfferUpgradeModalProps = {
  propertyContent: UI.Property;
  procuredOffer?: UI.ProcuredOffer | null;
  offer: UI.RoomOptionOffer | undefined;
  roomType: AvailabilityApi.ParsedResponse.RoomType | undefined;
  isOpen: boolean;
  onClose: () => void;
  title: string;
  cancelText: string;
  onCancel: () => void;
  submitText: string;
  onSubmit: () => void;
};

const CtaButton = styled(Button)`
  font-size: 1rem;
  line-height: 1.5;
  padding: 6px 0.5rem !important;
  letter-spacing: 1.5px;
`;

const OfferModal = ({
  roomType,
  offer,
  isOpen,
  onClose,
  title,
  propertyContent,
  procuredOffer,
  cancelText,
  onCancel,
  submitText,
  onSubmit,
}: OfferUpgradeModalProps) => {
  const shouldUseNewEvents = useGA4Events();

  if (!roomType || !offer) {
    return null;
  }

  const onClick = () => {
    if (shouldUseNewEvents) {
      dataLayer.ctaClickEvent({
        itemText: submitText,
      });
    }
    onSubmit();
  };

  const { images, mainImage, name: roomName } = roomType || {};
  const { name: hotelName, ratingType, rating, tags } = propertyContent;
  const { leadInOffer, hotelOffer } = offer;
  const inclusions = leadInOffer?.inclusions ?? hotelOffer?.inclusions;

  return isOpen ? (
    <Modal onClose={onClose}>
      <Modal.Header>{title}</Modal.Header>
      <Modal.Body pt="4" px={['4', null, '5']} pb={['4', null, '6']}>
        <Flex flexDirection={['column', null, 'row']} gap="6">
          <Pictures
            roomName={roomName}
            images={images}
            mainImage={mainImage}
            width={['100%', null, '300px']}
            height="200px"
          />
          <Flex
            width="100%"
            gap={4}
            flexDirection="column"
            alignItems="flex-start"
          >
            <Heading
              tags={tags}
              name={hotelName}
              rating={rating}
              ratingType={ratingType}
            />
            <RoomType name={roomName} />
            <PackageInclusions
              propertyContent={propertyContent}
              inclusions={inclusions}
              procuredOffer={procuredOffer}
            />
            <OfferPrice offer={offer} includeDivider={false} />
          </Flex>
        </Flex>
        <Flex
          flexDirection={['column-reverse', null, 'row']}
          mt="6"
          gap="4"
          justifyContent="flex-end"
        >
          <CtaButton
            variant="primary"
            // @ts-expect-error outline is valid but not inferred correctly
            outline
            width={['100%', null, '193.5px']}
            onClick={onCancel}
          >
            {cancelText}
          </CtaButton>
          <CtaButton
            variant="primary"
            width={['100%', null, '193.5px']}
            onClick={onClick}
          >
            {submitText}
          </CtaButton>
        </Flex>
      </Modal.Body>
    </Modal>
  ) : null;
};

OfferModal.Upgrade = (
  props: Omit<OfferUpgradeModalProps, 'title' | 'cancelText' | 'submitText'>,
) => (
  <OfferModal
    title="Upgrade your package"
    cancelText="No thanks"
    submitText="Upgrade package"
    {...props}
  />
);

OfferModal.OtherOption = (
  props: Omit<OfferUpgradeModalProps, 'title' | 'cancelText' | 'submitText'>,
) => (
  <OfferModal
    title="Other package option"
    cancelText="Cancel"
    submitText="Select package"
    {...props}
  />
);

export default OfferModal;
